import { useState, useCallback, useEffect } from "react";
import { BarChart2, Receipt, Plus, Trash2, Clock, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { usePopup } from "@/components/AppPopups";
import api from "@/lib/api";
import { hasAnyPermission, hasPermission } from "@/lib/permissions";
import { inr } from "@/lib/format";
import { Modal } from "./Modal";
import { toMonthInput, fmtMonth } from "../utils/calendarHelpers";

const EXPENSE_TYPES = ["Rent", "Maintenance", "Electricity", "Other"];
const EXPENSE_TYPE_COLORS = {
  Rent: "bg-red-100 text-blue-700",
  Maintenance: "bg-amber-100 text-amber-700",
  Electricity: "bg-blue-100 text-blue-700",
  Other: "bg-slate-100 text-slate-700",
};

function FinKpi({ label, value, sub, color = "slate", big = false }) {
  const colors = {
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
    rose:    "bg-rose-50 border-rose-100 text-rose-700",
    indigo:  "bg-blue-50 border-blue-100 text-blue-700",
    violet:  "bg-violet-50 border-violet-100 text-violet-700",
    slate:   "bg-slate-50 border-slate-100 text-slate-700",
  };
  const valColors = {
    emerald: "text-emerald-700", rose: "text-rose-600", indigo: "text-blue-700",
    violet: "text-violet-700",   slate: "text-slate-800",
  };
  return (
    <div className={`rounded-lg border p-3 ${colors[color]}`}>
      <div className="text-[10px] font-bold uppercase tracking-widest opacity-70 mb-0.5">{label}</div>
      <div className={`font-mono font-bold ${big ? "text-lg" : "text-sm"} ${valColors[color]}`}>{value}</div>
      {sub && <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}

function AddExpenseModal({ onClose, onSave }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ expense_type: "Maintenance", amount: "", date: today, notes: "" });
  const [loading, setLoading] = useState(false);
  const inputCls = "mt-1.5 w-full px-3.5 py-2.5 rounded-lg border text-sm text-slate-900 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-400 transition-all";
  const inputStyle = { borderColor: "var(--hx-border)" };

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Amount must be greater than 0"); return; }
    setLoading(true);
    try {
      await onSave({ ...form, amount: Number(form.amount) });
      onClose();
    } catch {
      // handled upstream
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal title="Add Expense" onClose={onClose} accent="red">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Expense Type</label>
          <select value={form.expense_type} onChange={e => setForm(f => ({ ...f, expense_type: e.target.value }))} className={inputCls} style={inputStyle}>
            {EXPENSE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Amount (₹)</label>
            <input type="number" min="1" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required placeholder="0.00" className={inputCls} style={inputStyle} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</label>
            <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required className={inputCls} style={inputStyle} />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Notes</label>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Optional description…" className={`${inputCls} resize-none`} style={inputStyle} />
        </div>
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="hx-secondary-action flex-1">Cancel</button>
          <button type="submit" disabled={loading} className="hx-primary-action flex-1">
            {loading ? "Saving…" : "Add Expense"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function HoardingFinancials({ hoardingId, user }) {
  const popup = usePopup();
  const today = new Date();
  const [month, setMonth] = useState(toMonthInput(today));
  const [fin, setFin] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showSnapshots, setShowSnapshots] = useState(false);

  const canViewFinancials = hasAnyPermission(user, ["view_basic_revenue", "view_campaign_amounts", "view_rent_details", "view_expenses", "view_profit_and_loss", "view_financial_reports"]);
  const canViewRent = hasAnyPermission(user, ["view_rent_details", "view_profit_and_loss", "view_financial_reports"]);
  const canViewExpenses = hasAnyPermission(user, ["view_expenses", "view_profit_and_loss", "view_financial_reports"]);
  const canCreateExpense = hasPermission(user, "create_expenses");
  const canDeleteExpense = hasPermission(user, "delete_expenses");
  const visibleExpenses = canViewExpenses ? expenses : [];

  const loadFin = useCallback(() =>
    canViewFinancials
      ? api.get(`/hoardings/${hoardingId}/financials?month=${month}`, { skipGlobalLoader: true }).then(r => setFin(r.data))
      : Promise.resolve(),
  [hoardingId, month, canViewFinancials]);

  const loadExpenses = useCallback(() =>
    canViewExpenses
      ? api.get(`/hoardings/${hoardingId}/expenses?month=${month}`, { skipGlobalLoader: true }).then(r => setExpenses(r.data || []))
      : Promise.resolve(setExpenses([])),
  [hoardingId, month, canViewExpenses]);

  const loadSnapshots = useCallback(() =>
    canViewFinancials
      ? api.get(`/hoardings/${hoardingId}/snapshots`, { skipGlobalLoader: true }).then(r => setSnapshots(r.data || []))
      : Promise.resolve(setSnapshots([])),
  [hoardingId, canViewFinancials]);

  useEffect(() => {
    loadFin();
    loadExpenses();
  }, [loadFin, loadExpenses]);

  useEffect(() => {
    if (showSnapshots) loadSnapshots();
  }, [showSnapshots, loadSnapshots]);

  async function handleAddExpense(payload) {
    try {
      await api.post(`/hoardings/${hoardingId}/expenses`, payload);
      toast.success("Expense added");
      await Promise.all([loadFin(), loadExpenses()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save expense");
      throw e;
    }
  }

  async function handleDeleteExpense(expId) {
    const ok = await popup.confirm({
      title: "Remove this expense?",
      description: "This expense entry will be removed from the hoarding financials.",
      confirmText: "Remove Expense",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/hoardings/${hoardingId}/expenses/${expId}`);
      toast.success("Expense removed");
      await Promise.all([loadFin(), loadExpenses()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  }

  if (!canViewFinancials) return null;

  const isProfit = fin ? fin.profit >= 0 : true;

  return (
    <div className="hx-data-panel">
      <header>
        <div className="flex items-center gap-2.5">
          <div className="hx-metric-icon tone-indigo">
            <BarChart2 size={17} />
          </div>
          <div>
            <h2>Profitability & ROI</h2>
            <p>{fmtMonth(month)}</p>
          </div>
        </div>
        <input
          type="month"
          value={month}
          onChange={e => setMonth(e.target.value)}
          className="text-xs bg-white border rounded-lg px-3 py-2 text-slate-700 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-400 transition-all"
          style={{ borderColor: "var(--hx-border)" }}
        />
      </header>

      <div className="space-y-5">
        {fin ? (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            <FinKpi label="Revenue" value={inr(fin.revenue)} sub={`${fin.booked_days}/${fin.days_in_month} days booked`} color="emerald" />
            <FinKpi label="Monthly Rent" value={canViewRent ? inr(fin.monthly_rent) : "Hidden"} sub={canViewRent ? "Fixed cost to owner" : "No rent access"} color="slate" />
            <FinKpi label="Other Expenses" value={canViewExpenses ? inr(fin.other_expenses) : "Hidden"} sub={canViewExpenses ? `${visibleExpenses.length} entr${visibleExpenses.length === 1 ? "y" : "ies"}` : "No expense access"} color="slate" />
            <FinKpi label={isProfit ? "Net Profit" : "Net Loss"} value={inr(Math.abs(fin.profit))} sub={canViewRent || canViewExpenses ? `Total expenses: ${inr(fin.total_expenses)}` : "Limited finance view"} color={isProfit ? "emerald" : "rose"} big />
            <div className="grid grid-rows-2 gap-3">
              <FinKpi label="ROI" value={`${fin.roi_pct.toFixed(1)}%`} color={isProfit ? "indigo" : "rose"} />
              <FinKpi label="Occupancy" value={`${fin.occupancy_pct.toFixed(0)}%`} color="violet" />
            </div>
          </div>
        ) : (
          <div className="h-24 rounded-lg bg-slate-100 animate-pulse" />
        )}

        {fin && (
          <div className="flex items-center gap-3 p-4 rounded-lg border" style={{ background: "var(--hx-bg)", borderColor: "var(--hx-border)" }}>
            {isProfit ? <TrendingUp size={18} className="text-emerald-600 flex-shrink-0" /> : <TrendingDown size={18} className="text-rose-500 flex-shrink-0" />}
            <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
              {fin.total_expenses > 0 && (
                <div
                  className={`h-full rounded-full transition-all duration-500 ${isProfit ? "bg-emerald-500" : "bg-rose-500"}`}
                  style={{ width: `${Math.min(100, Math.abs(fin.roi_pct))}%` }}
                />
              )}
            </div>
            <span className={`text-xs font-bold font-mono flex-shrink-0 ${isProfit ? "text-emerald-700" : "text-rose-600"}`}>
              {isProfit ? "+" : "-"}{Math.abs(fin.roi_pct).toFixed(1)}% ROI
            </span>
          </div>
        )}

        {canViewExpenses && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Receipt size={15} className="text-slate-500" />
                <span className="text-sm font-semibold text-slate-800">Expenses — {fmtMonth(month)}</span>
              </div>
              {canCreateExpense && (
                <button onClick={() => setShowExpenseModal(true)} className="hx-primary-action px-3.5 py-2 text-xs">
                  <Plus size={13} /> Add Expense
                </button>
              )}
            </div>

            {visibleExpenses.length > 0 ? (
              <div className="space-y-1.5">
                {visibleExpenses.map(e => (
                  <div key={e.id} className="flex items-center justify-between px-4 py-2.5 rounded-lg border group" style={{ background: "var(--hx-bg)", borderColor: "var(--hx-border)" }}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${EXPENSE_TYPE_COLORS[e.expense_type] || "bg-slate-100 text-slate-600"}`}>{e.expense_type}</span>
                      <span className="text-xs text-slate-500 flex-shrink-0">{e.date}</span>
                      {e.notes && <span className="text-xs text-slate-400 truncate">{e.notes}</span>}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                      <span className="hx-money-cell text-sm">{inr(e.amount)}</span>
                      {canDeleteExpense && (
                        <button onClick={() => handleDeleteExpense(e.id)} className="opacity-0 group-hover:opacity-100 p-1 rounded-lg hover:bg-rose-50 text-rose-500 transition-all">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="hx-empty-state border border-dashed rounded-lg" style={{ borderColor: "var(--hx-border)" }}>
                No expenses recorded for {fmtMonth(month)}
              </div>
            )}
          </div>
        )}

        <div>
          <button onClick={() => setShowSnapshots(s => !s)} className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1.5 transition-colors">
            <Clock size={13} />
            {showSnapshots ? "Hide" : "Show"} Monthly Snapshot History
          </button>

          {showSnapshots && (
            <div className="hx-table-surface mt-3">
              <table className="text-xs">
                <thead>
                  <tr>
                    {["Month", "Revenue", "Rent", "Other Exp.", "Total Exp.", "Profit/Loss", "ROI", "Occupancy"].map(h => (
                      <th key={h} className="whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {snapshots.length === 0 ? (
                    <tr><td colSpan={8} className="py-8 text-center text-slate-400">No snapshots recorded yet.</td></tr>
                  ) : snapshots.map(s => (
                    <tr key={s.id}>
                      <td className="font-semibold text-slate-800 whitespace-nowrap">{fmtMonth(s.month)}</td>
                      <td className="hx-money-cell text-emerald-700">{inr(s.revenue)}</td>
                      <td className="hx-money-cell text-slate-600">{inr(s.monthly_rent)}</td>
                      <td className="hx-money-cell text-slate-600">{inr(s.other_expenses)}</td>
                      <td className="hx-money-cell text-slate-600">{inr(s.total_expenses)}</td>
                      <td className={`hx-money-cell ${s.profit >= 0 ? "text-emerald-700" : "text-rose-600"}`}>
                        {s.profit >= 0 ? "+" : ""}{inr(s.profit)}
                      </td>
                      <td className={`font-mono font-semibold ${s.roi_pct >= 0 ? "text-blue-700" : "text-rose-600"}`}>
                        {s.roi_pct.toFixed(1)}%
                      </td>
                      <td className="font-mono text-violet-700">{s.occupancy_pct.toFixed(0)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showExpenseModal && canCreateExpense && (
        <AddExpenseModal onClose={() => setShowExpenseModal(false)} onSave={handleAddExpense} />
      )}
    </div>
  );
}
